import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Check, Copy, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function MediaPickerDialog({ open, onClose, onSelect, existingUrls = [] }) {
    const [images, setImages] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const load = useCallback(async (pageCursor = null, append = false) => {
        if (append) setLoadingMore(true);
        else setLoading(true);
        try {
            const params = { max_results: 30 };
            if (pageCursor) params.cursor = pageCursor;
            const { data } = await api.get("/admin/cloudinary/images", { params });
            setImages((prev) => (append ? [...prev, ...data.images] : data.images));
            setNextCursor(data.nextCursor || null);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load media library");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        if (open) load(null, false);
        else {
            setImages([]);
            setNextCursor(null);
        }
    }, [open, load]);

    if (!open) return null;

    const copyUrl = (url, e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(url).then(() => toast.success("URL copied"));
    };

    const handlePick = (url) => {
        if (existingUrls.includes(url)) {
            toast.message("Already added to this product");
            return;
        }
        onSelect(url);
        onClose();
        toast.success("Image added");
    };

    return (
        <ModalOverlay onClose={onClose}>
            <div
                className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
                data-testid="media-picker-dialog"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-sage-300/30">
                    <h3 className="font-serif text-xl text-ink-900">Choose from media library</h3>
                    <button type="button" onClick={onClose} className="text-ink-500 hover:text-ink-800" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 pb-4">
                    {loading && images.length === 0 ? (
                        <LoadingSpinner />
                    ) : images.length === 0 ? (
                        <p className="text-sm text-ink-500 py-12 text-center">No images in Cloudinary yet. Upload one first.</p>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 pt-4">
                            {images.map((img) => {
                                const added = existingUrls.includes(img.url);
                                return (
                                    <button
                                        key={img.publicId}
                                        type="button"
                                        onClick={() => handlePick(img.url)}
                                        disabled={added}
                                        className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-colors ${
                                            added
                                                ? "border-sage-400 opacity-60 cursor-default"
                                                : "border-transparent hover:border-sage-500"
                                        }`}
                                    >
                                        <img src={img.url} alt="" className="h-full w-full object-cover" />
                                        {added && (
                                            <span className="absolute inset-0 flex items-center justify-center bg-ink-900/40">
                                                <Check className="text-white h-6 w-6" />
                                            </span>
                                        )}
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => copyUrl(img.url, e)}
                                            onKeyDown={(e) => e.key === "Enter" && copyUrl(img.url, e)}
                                            className="absolute bottom-1 right-1 p-1 rounded-md bg-white/90 text-ink-700 hover:bg-white"
                                            title="Copy URL"
                                        >
                                            <Copy size={12} />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                {nextCursor && (
                    <div className="px-6 py-4 border-t border-sage-300/30 flex justify-center">
                        <button
                            type="button"
                            onClick={() => load(nextCursor, true)}
                            disabled={loadingMore}
                            className="btn-secondary text-sm"
                        >
                            {loadingMore ? "Loading…" : "Load more"}
                        </button>
                    </div>
                )}
            </div>
        </ModalOverlay>
    );
}

function ModalOverlay({ onClose, children }) {
    return (
        <div
            className="fixed inset-0 z-[70] bg-ink-900/40 flex items-center justify-center p-4"
            onClick={onClose}
            role="presentation"
        >
            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                {children}
            </div>
        </div>
    );
}

function LoadingSpinner() {
    return (
        <div className="flex justify-center py-16 text-ink-500">
            <Loader2 className="h-8 w-8 animate-spin" />
        </div>
    );
}
